import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import backend from '../../services/backend';
import { Card } from '../../components/Card';
import { ClusterOutlierStrip } from './ClusterOutlierStrip';
import { ClusterOutlierList } from './ClusterOutlierList';
import { ClusterEmbeddingMap } from './ClusterEmbeddingMap';

export function ClusterOutliersCard({
  categoryId,
  onSelectCluster,
}: {
  categoryId: string;
  onSelectCluster: (id: number) => void;
}) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  // Its own cache key, NOT CategoryClusterView's ['categoryClusters', …]: the two
  // endpoints answer different shapes, so sharing one would serve whichever
  // component queried second the other's payload (DR-2809).
  const outliersQuery = useQuery({
    queryKey: ['categoryClusterOutliers', categoryId],
    queryFn: () => backend.getCategoryClusterOutliers(categoryId),
    enabled: !!categoryId,
  });

  const result = outliersQuery.data ?? { clusters: [], threshold: null };

  return (
    <Card header={<h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Cluster Outliers</h2>}>
      {outliersQuery.isLoading ? (
        <div className="h-[300px] animate-pulse rounded-lg bg-gray-100 dark:bg-zinc-700/40" />
      ) : result.clusters.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No clusters to display.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <ClusterOutlierStrip
            points={result.clusters}
            threshold={result.threshold}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            onSelect={onSelectCluster}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ClusterOutlierList
              points={result.clusters}
              hoveredId={hoveredId}
              onHover={setHoveredId}
              onSelect={onSelectCluster}
            />
            <ClusterEmbeddingMap
              points={result.clusters}
              threshold={result.threshold}
              hoveredId={hoveredId}
              onHover={setHoveredId}
              onSelect={onSelectCluster}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
